import { ArrowLeftIcon, PencilIcon, ArrowPathIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid'
import profileImage from 'assets/images/users/erica.jpg'
import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_USER_LIST } from 'graphql/users/queries'
import { UPDATE_USER_STATUS, CREATE_ADMIN_USER } from 'graphql/users/mutations'
import { t } from 'i18next'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { Badge } from 'components/badge'
import { LANGUAGES } from 'pages/constants'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import placeHolderImage from 'assets/images/users/profile_default.png'

export default function UserDetails() {
    // GEt the user id from the url and fetch the user details
    const { id } = useParams()
    const { pool } = useParams()
    const navigate = useNavigate()
    const [userList, setUserList] = useState(null)
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [confirmMessage, setConfirmMessage] = useState('')

    // for Status update
    const [statusUserId, setStatusUserId] = useState('')
    const [userStatus, setUserStatus] = useState(false)

    const [getUserList, { loading: userListLoading, error: userListError }] = useLazyQuery(GET_USER_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchUser.success && data.searchUser.code === '200') {
                console.log('User List Data:', data.searchUser.users)
                setUserList(data.searchUser.users)
            } else {
                setErrorMessage('Problem updating the user status. Please try again in a few moments.')
                setShowErrDialog(true)
            }
        },
    })

    useEffect(() => {
        searchUsers()
    }, [id])

    // Search users User List
    const searchUsers = () => {
        getUserList({
            variables: {
                searchUserId: id,
                role: null,
                userPool: 'admin',
                page: {
                    offset: 0,
                    limit: 1,
                },
                searchString: '',
            },
        })
    }

    // Update User Status
    const [updateUserStatus] = useMutation(UPDATE_USER_STATUS, {
        onCompleted: data => {
            if (data?.updateUserStatus?.code === '200') {
                setSuccessMessage(data?.updateUserStatus?.message)
                setShowSucDialog(true)
                setTimeout(() => {
                    searchUsers()
                }, 500)
            } else {
                setErrorMessage('Problem updating the user status. Please try again in a few moments.')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage('Problem updating the user status. Please try again in a few moments.')
            setShowErrDialog(true)
        },
    })
    const onEnableDisableUser = (userid, status) => {
        updateUserStatus({
            variables: {
                userPool: 'admin',
                userId: userid,
                enabled: status,
                remarks: status + ' user',
            },
        })
    }

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }

    // Disable User
    const handleDisableUser = (userId, status) => {
        setStatusUserId(userId)
        setUserStatus(status)
        const msg = status ? 'enable' : 'disable'
        setConfirmMessage(`Are you sure you want to ${msg} this user?`)
        setShowConfirmDialog(true)
        return true
    }

    const handleSucClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
    }
    const handleConfirmPositive = () => {
        onEnableDisableUser(statusUserId, userStatus)
        setShowConfirmDialog(false)
    }

    const handleConfirmNegative = () => {
        setShowConfirmDialog(false)
    }

    const handleBack = () => {
        navigate('/users')
    }

    // Add mutation for resend code
    const [createAdminUserInfo, { loading: createUserLoading }] = useMutation(CREATE_ADMIN_USER, {
        onCompleted: data => {
            if (data?.createAdminUser.code === '200' && data?.createAdminUser.success === true) {
                setSuccessMessage('Code sent successfully')
                setShowSucDialog(true)
            } else {
                setErrorMessage('Problem resending the code. Please try again.')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage('Problem resending the code. Please try again.')
            setShowErrDialog(true)
        },
    })

    // Add handler for resend code
    const handleResendCode = userId => {
        const profile_info = userList[0]
        const formattedData = {
            additionalInfo: {
                job_title: null,
                document_number: null,
                document_type_code: null,
                document: null,
                profile_photo: profile_info.additional_info.profile_photo,
            },
            userRoles: profile_info.user_roles,
            user: {
                email: profile_info.email,
                family_name: profile_info.user_info.family_name,
                given_name: profile_info.user_info.given_name,
                locale: profile_info.user_info.locale,
                phone_number: profile_info.user_info.phone_number,
                resend_code: true,
            },
        }
        createAdminUserInfo({
            variables: formattedData,
        })
    }

    return (
        <>
            <div className="mb-6 flex justify-normal items-center">
                <button
                    onClick={handleBack}
                    type="button"
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <h3 className="text-lg font-bold leading-7 text-primary ps-2.5 pb-1">User details</h3>
            </div>
            {userListLoading && <div className="flex justify-center items-center bg-white py-5">Loading ....</div>}
            {userListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch user details!')}</div>
                </div>
            )}
            {!userListLoading && !userListError && userList?.length === 0 && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('No data found!')}</div>
                </div>
            )}
            {!userListLoading && !userListError && userList?.length > 0 && (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        <div className="md:flex md:items-center md:justify-between md:space-x-5">
                            <div className="flex items-start space-x-5">
                                <div className="flex-shrink-0">
                                    <div className="relative">
                                        <ImageDisplay
                                            s3Key={userList[0].additional_info?.profile_photo?.s3_key}
                                            file=""
                                            placeHolderImage={placeHolderImage}
                                            classStyle="h-16 w-16 rounded-full"
                                        />
                                    </div>
                                </div>
                                <div className="pt-1.5">
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        {userList[0].user_info.given_name} {userList[0].user_info.family_name}
                                    </h1>
                                    <p className="text-sm font-medium text-gray-500">{userList[0].email}</p>
                                </div>
                            </div>
                            <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
                                {/* Edit Button */}
                                <button
                                    onClick={() => {
                                        navigate('/users/user-form', {
                                            state: {
                                                userData: {
                                                    id: userList[0].id,
                                                    email: userList[0].email,
                                                    family_name: userList[0].user_info.family_name,
                                                    given_name: userList[0].user_info.given_name,
                                                    locale: userList[0].user_info.locale,
                                                    phone_number: userList[0].user_info.phone_number,
                                                    userRoles: userList[0].user_roles,
                                                    profile_photo: userList[0].additional_info?.profile_photo,
                                                    additional_info: userList[0].additional_info,
                                                },
                                            },
                                        })
                                    }}
                                    type="button"
                                    className="inline-flex items-center justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold text-primary shadow-sm ring-1 ring-inset ring-primary hover:bg-primary/[.1]"
                                >
                                    <PencilIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                    {t('Edit')}
                                </button>

                                {/* Enable/Disable Button */}
                                <button
                                    onClick={() => {
                                        handleDisableUser(userList[0].id, !userList[0].is_active)
                                    }}
                                    type="button"
                                    className={`inline-flex items-center justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-sm ${
                                        userList[0].is_active
                                            ? 'bg-rose-600 text-white hover:bg-rose-500'
                                            : 'bg-primary text-white hover:bg-primary/[.9]'
                                    }`}
                                >
                                    {userList[0].is_active ? (
                                        <EyeSlashIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                    ) : (
                                        <EyeIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                    )}
                                    {userList[0].is_active ? t('Disable') : t('Enable')}
                                </button>

                                {/* Resend Code Button */}
                                {userList[0].cognito_status === 'FORCE_CHANGE_PASSWORD' && (
                                    <button
                                        onClick={() => handleResendCode(userList[0].id)}
                                        type="button"
                                        className="inline-flex items-center justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold text-primary shadow-sm ring-1 ring-inset ring-primary hover:bg-primary/[.1]"
                                    >
                                        <ArrowPathIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                        {t('Resend Code')}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Personal Information')}
                                </h3>
                                <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                    {t('Personal Information of the user for contact purpose')}
                                </p>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">First Name</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {userList[0].user_info.given_name}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Last Name')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {userList[0].user_info.family_name}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Email Address')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {userList[0].email}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Mobile Number')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {userList[0].user_info.phone_number}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">User Profile</h3>
                                <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                    This is information which will define the access type and user access the system.
                                </p>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Access Role')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {userList[0].user_roles.map((role, index) => (
                                                <Badge key={index} color="blue" style={{ marginRight: '8px' }}>
                                                    {role}
                                                </Badge>
                                            ))}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">Status</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <Badge color="lime">{userList[0].cognito_status}</Badge>
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">User Status</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <Badge color={userList[0].is_active ? 'emerald' : 'rose'}>
                                                {userList[0].is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Preferred Language')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {userList[0].user_info.locale === 'ar' ? LANGUAGES.ar : LANGUAGES.en}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        {/* Passport Details Section */}
                        {userList[0].additional_info?.attachments?.find(a => a.document_type === 'PASSPORT') && (
                            <div className="mt-8">
                                <div className="px-4 sm:px-0">
                                    <h3 className="text-lg font-semibold leading-7 text-primary">
                                        {t('Passport Details')}
                                    </h3>
                                    <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                        {t('Passport information of the user')}
                                    </p>
                                </div>
                                <div className="mt-3 border-t border-gray-100">
                                    <dl className="divide-y divide-gray-100">
                                        <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Passport Number')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {userList[0].additional_info.attachments.find(a => a.document_type === 'PASSPORT')?.document_number || '-'}
                                            </dd>
                                        </div>
                                        <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Passport Attachment')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {userList[0].additional_info.attachments.find(a => a.document_type === 'PASSPORT')?.document ? (
                                                    <div className="flex flex-col gap-3">
                                                        <div className="group h-[200px] w-full max-w-md overflow-hidden rounded-lg bg-gray-100 border border-gray-200">
                                                            <ImageDisplay
                                                                s3Key={userList[0].additional_info.attachments.find(a => a.document_type === 'PASSPORT').document.s3_key}
                                                                file=""
                                                                classStyle="object-contain w-full h-full group-hover:opacity-90 transition-opacity"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-900 font-medium">
                                                                {userList[0].additional_info.attachments.find(a => a.document_type === 'PASSPORT').document.file_name}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                ({(userList[0].additional_info.attachments.find(a => a.document_type === 'PASSPORT').document.size / 1024 / 1024).toFixed(2)} MB)
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : '-'}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        )}

                        {/* National ID Details Section */}
                        {userList[0].additional_info?.attachments?.find(a => a.document_type === 'NATIONAL_ID') && (
                            <div className="mt-8">
                                <div className="px-4 sm:px-0">
                                    <h3 className="text-lg font-semibold leading-7 text-primary">
                                        {t('National ID Details')}
                                    </h3>
                                    <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                        {t('National ID information of the user')}
                                    </p>
                                </div>
                                <div className="mt-3 border-t border-gray-100">
                                    <dl className="divide-y divide-gray-100">
                                        <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('National ID Number')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {userList[0].additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID')?.document_number || '-'}
                                            </dd>
                                        </div>
                                        <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('National ID Attachment')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {userList[0].additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID')?.document ? (
                                                    <div className="flex flex-col gap-3">
                                                        <div className="group h-[200px] w-full max-w-md overflow-hidden rounded-lg bg-gray-100 border border-gray-200">
                                                            <ImageDisplay
                                                                s3Key={userList[0].additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID').document.s3_key}
                                                                file=""
                                                                classStyle="object-contain w-full h-full group-hover:opacity-90 transition-opacity"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-900 font-medium">
                                                                {userList[0].additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID').document.file_name}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                ({(userList[0].additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID').document.size / 1024 / 1024).toFixed(2)} MB)
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : '-'}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title="Error"
                    message={errorMessage}
                    onClose={handleErrClose}
                    isMentee={false}
                />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleSucClose} />
            )}
            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title="User Status Update"
                    negativeActionText="Cancel"
                    positiveActionText="Yes"
                    message={confirmMessage}
                    onPositive={handleConfirmPositive}
                    onNegative={handleConfirmNegative}
                />
            )}
            {createUserLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
